import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ContentState, Content, UploadItem } from './types';
import { LoadingStatus } from '@/types';

const initialState: ContentState = {
  data: [],
  status: LoadingStatus.IDLE,
  error: null,
  currentUpload: {
    status: LoadingStatus.IDLE,
    progress: 0,
    content: null,
    error: null
  },
  uploads: []
};

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    // Set all content
    setContent: (state, action: PayloadAction<Content[]>) => {
      state.data = action.payload;
      state.status = LoadingStatus.SUCCEEDED;
      state.error = null;
    },
    
    // Add a single content item
    addContent: (state, action: PayloadAction<Content>) => {
      if (state.data) {
        state.data.unshift(action.payload);
      } else {
        state.data = [action.payload];
      }
    },
    
    // Remove a content item
    removeContent: (state, action: PayloadAction<string>) => {
      if (state.data) {
        state.data = state.data.filter(item => item.id !== action.payload);
      }
    },
    
    // Update a content item
    updateContent: (state, action: PayloadAction<Content>) => {
      if (state.data) {
        state.data = state.data.map(item => 
          item.id === action.payload.id ? action.payload : item
        );
      }
    },
    
    // Set content loading status
    setContentStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.status = action.payload;
    },
    
    // Set content error
    setContentError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.status = action.payload ? LoadingStatus.FAILED : state.status;
    },
    
    // Upload started
    uploadStarted: (state) => {
      state.currentUpload.status = LoadingStatus.LOADING;
      state.currentUpload.progress = 0;
      state.currentUpload.error = null;
    },
    
    // Upload progress
    uploadProgress: (state, action: PayloadAction<number>) => {
      state.currentUpload.progress = action.payload;
    },
    
    // Upload success
    uploadSuccess: (state, action: PayloadAction<Content>) => {
      state.currentUpload.status = LoadingStatus.SUCCEEDED;
      state.currentUpload.content = action.payload;
      state.currentUpload.progress = 100;
      state.currentUpload.error = null;
      
      // Also add to content list
      if (state.data) {
        state.data.unshift(action.payload);
      } else {
        state.data = [action.payload];
      }
    },
    
    // Upload failed
    uploadFailed: (state, action: PayloadAction<string>) => {
      state.currentUpload.status = LoadingStatus.FAILED;
      state.currentUpload.error = action.payload;
    },
    
    // Reset upload state
    resetUpload: (state) => {
      state.currentUpload = {
        status: LoadingStatus.IDLE,
        progress: 0,
        content: null,
        error: null
      };
    },
    
    // Add new upload to queue
    addUpload: (state, action: PayloadAction<{ id: string; file: File }>) => {
      const newUpload: UploadItem = {
        id: action.payload.id,
        file: action.payload.file,
        status: LoadingStatus.LOADING,
        progress: 0,
        content: null,
        error: null,
        startedAt: new Date()
      };
      state.uploads.push(newUpload);
    },
    
    // Update upload progress
    updateUploadProgress: (state, action: PayloadAction<{ id: string; progress: number }>) => {
      const upload = state.uploads.find(u => u.id === action.payload.id);
      if (upload) {
        upload.progress = action.payload.progress;
      }
    },
    
    // Upload completed successfully
    completeUpload: (state, action: PayloadAction<{ id: string; content: Content }>) => {
      const upload = state.uploads.find(u => u.id === action.payload.id);
      if (upload) {
        upload.status = LoadingStatus.SUCCEEDED;
        upload.content = action.payload.content;
        upload.progress = 100;
        upload.error = null;
      }
      
      // Also add to content list
      if (state.data) {
        state.data.unshift(action.payload.content);
      } else {
        state.data = [action.payload.content];
      }
    },
    
    // Upload failed
    failUpload: (state, action: PayloadAction<{ id: string; error: string }>) => {
      const upload = state.uploads.find(u => u.id === action.payload.id);
      if (upload) {
        upload.status = LoadingStatus.FAILED;
        upload.error = action.payload.error;
      }
    },
    
    // Remove upload from queue
    removeUpload: (state, action: PayloadAction<string>) => {
      state.uploads = state.uploads.filter(u => u.id !== action.payload);
    },
    
    // Clear all uploads
    clearUploads: (state) => {
      state.uploads = [];
    },

    // Clear all content data (used on logout)
    clearContentState: (state) => {
      state.data = [];
      state.status = LoadingStatus.IDLE;
      state.error = null;
      state.currentUpload = {
        status: LoadingStatus.IDLE,
        progress: 0,
        content: null,
        error: null
      };
      state.uploads = [];
    }
  }
});

export const { 
  setContent, 
  addContent, 
  removeContent, 
  updateContent, 
  setContentStatus,
  setContentError,
  uploadStarted,
  uploadProgress,
  uploadSuccess,
  uploadFailed,
  resetUpload,
  addUpload,
  updateUploadProgress,
  completeUpload,
  failUpload,
  removeUpload,
  clearUploads,
  clearContentState
} = contentSlice.actions;

export default contentSlice.reducer; 